import {
  Autocomplete,
  AutocompleteProps,
  AutocompleteRenderInputParams,
  Box,
  SxProps,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material';
import DOMPurify from 'dompurify';
import { CSSProperties, ReactNode, useState } from 'react';
import { Controller, UseFormTrigger } from 'react-hook-form';
import { getColor } from '../../../assets/theme';

/**
 * Interface for AutocompleteController props
 * @template T The type of the option items
 */
interface AutocompleteControllerProps<T> extends Omit<
  AutocompleteProps<T, true, true, true>,
  'renderInput' | 'options' | 'value' | 'onChange'
> {
  /** Name of the form field */
  name: string;
  /** React Hook Form control object */
  control: any;
  /** Default value for the field */
  defaultValue?: any;
  /** Array of options for the autocomplete */
  options: any;
  /** Function to get the label for an option */
  getOptionLabel?: (option: any) => any;
  /** Form errors object */
  errors: any;
  /** Label for the field */
  label?: string;
  /** Custom styles for the component */
  style?: CSSProperties | { [key: string]: CSSProperties } | any;
  /** onChange handler */
  onChange?: any;
  /** Whether the field is disabled */
  disabled?: any;
  /** Placeholder text for the input */
  placeholder?: string;
  /** Whether the autocomplete is in a loading state */
  loading?: boolean;
  /** Additional props for the TextField component */
  textFieldProps?: TextFieldProps;
  /** Whether to show only border error and not error message */
  borderErrorOnly?: boolean;
  /** Whether to capitalize the text input */
  capitalizeText?: boolean;
  trigger?: UseFormTrigger<any>;
  /** Shows the label instead of the value */
  useLabel?: boolean;
  /** Flag to numbers only */
  numbersOnly?: boolean;
  helperText?: ReactNode;
  wrapperStyle?: SxProps;
  renderInput?: (params: AutocompleteRenderInputParams) => ReactNode;
}

/**
 * AutoCompleteController component
 * A customizable autocomplete input field integrated with React Hook Form
 *
 * @template T The type of the option items
 * @param {AutocompleteControllerProps<T>} props - The props for the component
 * @returns {JSX.Element} The rendered AutoCompleteController
 */
const AutoCompleteController = <T,>({
  control,
  name,
  options,
  errors,
  label,
  style = {},
  onChange: setValue,
  disabled,
  placeholder,
  defaultValue = null,
  loading = false,
  textFieldProps,
  borderErrorOnly = false,
  capitalizeText = false,
  getOptionLabel = (option: any) =>
    option?.label || option?.value || option?.toString(),
  trigger,
  numbersOnly = false,
  useLabel,
  helperText,
  wrapperStyle,

  ...etc
}: AutocompleteControllerProps<T>) => {
  const { danger } = getColor();

  const additionalStyle: CSSProperties | any = textFieldProps?.sx
    ? textFieldProps.sx
    : {};

  const [highlight, setHighlight] = useState('');

  // Define number-only keydown handler
  const handleNumberOnlyKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'Tab',
    ];
    if (!/[0-9]/.test(event.key) && !allowedKeys.includes(event.key)) {
      event.preventDefault();
    }
  };

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onChange, value, ref } }) => {
        return (
          <Box sx={{ width: '100%', ...wrapperStyle }}>
            <Autocomplete
              getOptionLabel={getOptionLabel}
              loading={loading}
              size='small'
              options={options}
              value={
                useLabel
                  ? options?.find(({ value: v }) => v === value)?.label || value
                  : value
              }
              onChange={(_e: any, value) => {
                onChange(value);
                setValue && setValue(value);
              }}
              isOptionEqualToValue={(option, value) =>
                (option as { value: string })?.value ===
                  (value as { value: string })?.value ||
                option === value ||
                option == value
              }
              onInputChange={(_event: any, newValue) => {
                etc.freeSolo && onChange(newValue);
              }}
              onHighlightChange={(_event, option) => {
                if (option == null || etc.freeSolo) return;
                setHighlight(option as unknown as string);
              }}
              onKeyDown={(event) => {
                if (event.key !== 'Tab') return;
                if (!etc.freeSolo) {
                  onChange(highlight);
                  setValue?.(highlight);
                }

                {
                  etc?.onKeyDown;
                }
              }}
              disabled={disabled}
              {...etc}
              renderInput={
                etc?.renderInput
                  ? etc.renderInput
                  : (params) => (
                      <TextField
                        inputRef={ref}
                        {...params}
                        {...textFieldProps}
                        inputProps={{
                          ...params.inputProps,
                          ...textFieldProps?.inputProps,
                        }}
                        InputProps={{
                          ...params.InputProps,
                          ...textFieldProps?.InputProps,
                          startAdornment:
                            textFieldProps?.InputProps?.startAdornment,
                          onKeyDown: numbersOnly
                            ? handleNumberOnlyKeyDown
                            : undefined,
                        }}
                        InputLabelProps={{ shrink: true }}
                        variant={
                          etc?.readOnly ? 'standard' : textFieldProps?.variant
                        }
                        sx={[
                          {
                            // ".MuiInputBase-root": { height: "40px" },
                            '.MuiInputBase-root': {
                              input: {
                                textTransform: capitalizeText
                                  ? 'capitalize'
                                  : params?.inputProps?.style?.textTransform ||
                                    'none',
                                p: etc?.readOnly
                                  ? '2.5px 4px 2.5px 8px!important'
                                  : 'default',
                              },
                            },
                          },
                          additionalStyle,
                        ]}
                        label={
                          label ? (
                            <span
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(label),
                              }}
                            />
                          ) : undefined
                        }
                        placeholder={placeholder}
                        {...(helperText ? { helperText } : {})}
                      />
                    )
              }
              onBlur={(event) => {
                etc?.onBlur?.(event);

                if (trigger && errors?.[name]) {
                  trigger(name);
                }
              }}
              sx={[
                {
                  marginBottom: errors?.[name] ? 0.5 : 2,
                  '& .MuiOutlinedInput-root': {
                    '& > fieldset': {
                      borderColor: errors?.[name] && danger,
                    },
                  },
                  '& .MuiAutocomplete-endAdornment button': {
                    border: 0,
                    color: '#161C248F',
                    bgcolor: 'transparent',
                    visibility: etc?.readOnly ? 'hidden' : 'visible',
                  },
                  '& .MuiFormLabel-root, .MuiSvgIcon-root, .MuiTypography-root':
                    {
                      color: errors?.[name] ? danger : 'default',
                    },
                  '& .MuiInputAdornment-positionStart': {
                    width: '24px',
                    pl: '7px',
                    pr: '14px',
                  },
                },
                style,
                etc?.sx,
              ]}
            />
            {errors?.[name] && !borderErrorOnly && (
              <Typography
                sx={{
                  fontFamily: 'Poppins',
                  fontSize: 12,
                  marginBottom: 1,
                  marginLeft: 1,
                  fontWeight: 400,
                  lineHeight: '19.92px',
                  letterSpacing: '0.4px',
                  color: danger,
                }}
              >
                {errors?.[name]?.message}
              </Typography>
            )}
          </Box>
        );
      }}
    />
  );
};

export default AutoCompleteController;
